// @ts-nocheck

import React, { useCallback, useMemo } from 'react';
import styled, { css } from 'styled-components';

import type { GridsCheckType } from '../../types';

const common = css`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  border-radius: 50%;
  width: 2em;
  height: 2em;
  min-width: 2em;
  min-height: 2em;
  margin: 0;
  padding: 0;
  border: none;
`;

const Button = styled.button`
  && {
    ${common}
    background: ${({ $isDisabled, theme }) => {
      if ($isDisabled) {
        return theme.grids.colors.darkgrey5;
      }
      return theme.grids.colors.darkgrey3;
    }};
  }
`;

const AnimatedCheck = styled.div`
  ${common}
  position:absolute;
  top: 0;
  left: 0;
  background: ${({ theme }) => theme.grids.colors.redlight};
  z-index: 1;
  transition:
    transform 300ms cubic-bezier(0.84, 1.84, 0.7, 0.79),
    opacity 300ms cubic-bezier(0.84, 1.84, 0.7, 0.79);
  transform: ${({ $isChecked }) => ($isChecked ? 'none' : 'scale(0.5)')};
  opacity: ${({ $isChecked }) => ($isChecked ? 1 : 0)};
`;

const ReadOnly = styled.span`
  ${common}
  box-sizing: border-box;
  background: ${({ theme, $isDisabled, $isWin, $isLost, $isChecked }) => {
    if ($isDisabled) {
      return theme.grids.colors.darkgrey5;
    }
    if ($isWin) {
      return theme.grids.colors.greenwin;
    }
    if ($isLost) {
      return theme.grids.colors.redlight;
    }
    if ($isChecked) {
      return '#fff';
    }
    return theme.grids.colors.darkgrey3;
  }};
  border: ${({ $isWin, $isLost, $isCheckedButNoBase }) =>
    !$isWin && !$isLost && $isCheckedButNoBase ? 'solid 2px #fff' : 'none'};
`;

const Label = styled.span`
  position: relative;
  z-index: 2;
  display: inline-block;
  font-size: 1em;
  line-height: 1;
  font-family: BettingPoliticaExtraBold, sans-serif;
`;

const LabelEdition = styled(Label)`
  color: ${({ theme, $isChecked, $isDisabled }) => {
    if ($isChecked) {
      return '#fff';
    }
    if ($isDisabled || $isChecked) {
      return theme.grids.colors.darkgrey2;
    }
    return theme.grids.colors.middlegrey1;
  }};
`;

const LabelReadOnly = styled(Label)`
  color: ${({ theme, $isDisabled, $isChecked, $isWin, $isLost, $isCheckedButNoBase }) => {
    if ($isDisabled || $isChecked || $isWin) {
      return theme.grids.colors.darkgrey2;
    }
    if ($isLost || $isCheckedButNoBase) {
      return '#fff';
    }
    return theme.grids.colors.middlegrey1;
  }};
`;

type Props = {
  isChecked: boolean;
  isCheckedButNoBase: boolean;
  hasOccured?: boolean | null;
  isDisabled?: boolean;
  typeCheck: GridsCheckType;
  matchIndex?: number;
  handleClick?: (matchIndex: number, typeCheck: GridsCheckType) => void;
  className?: string;
};

const CheckBox = ({
  isChecked,
  isCheckedButNoBase,
  hasOccured,
  isDisabled,
  typeCheck,
  matchIndex = null,
  handleClick,
  className,
}: Props) => {
  const isWin = useMemo(
    () => hasOccured !== null && hasOccured && (isCheckedButNoBase || isChecked),
    [hasOccured, isChecked, isCheckedButNoBase],
  );
  const isLost = useMemo(
    () => hasOccured !== null && hasOccured && !isCheckedButNoBase && !isChecked,
    [hasOccured, isChecked, isCheckedButNoBase],
  );
  const isEdition = useMemo(() => !!handleClick, [handleClick]);

  const onClick = useCallback(() => {
    if (handleClick && matchIndex !== null) {
      handleClick(matchIndex, typeCheck);
    }
  }, [matchIndex, handleClick, typeCheck]);

  const finalClassName = className ? `${className} grid-check` : 'grid-check';

  return isEdition ? (
    <Button className={finalClassName} $isDisabled={isDisabled} onClick={onClick} data-testid="grid-check-button">
      <AnimatedCheck $isChecked={isChecked} data-testid={isChecked ? 'grid-check-checked' : 'grid-check-unchecked'} />
      <LabelEdition $isChecked={isChecked} $isDisabled={isDisabled}>
        {typeCheck}
      </LabelEdition>
    </Button>
  ) : (
    <ReadOnly
      className={finalClassName}
      $isChecked={hasOccured || isChecked}
      $isCheckedButNoBase={isCheckedButNoBase}
      $isDisabled={isDisabled}
      $isWin={isWin}
      $isLost={isLost}
    >
      <LabelReadOnly
        $isChecked={isChecked}
        $isCheckedButNoBase={isCheckedButNoBase}
        $isDisabled={isDisabled}
        $isWin={isWin}
        $isLost={isLost}
      >
        {typeCheck}
      </LabelReadOnly>
    </ReadOnly>
  );
};

export default CheckBox;
